import React, { useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { navigationColors } from '../../styles/colors';

/** Google / OAuth photo from Supabase user metadata (when present). */
export function getAuthAvatarUrl(user: User | null | undefined): string | null {
  if (!user) return null;
  const meta = user.user_metadata || {};
  const fromMeta =
    (typeof meta.avatar_url === 'string' && meta.avatar_url) ||
    (typeof meta.picture === 'string' && meta.picture) ||
    null;
  if (fromMeta) return fromMeta;

  const identity = user.identities?.find((i) => i.provider === 'google')
    || user.identities?.[0];
  const data = identity?.identity_data as Record<string, unknown> | undefined;
  if (!data) return null;
  if (typeof data.avatar_url === 'string' && data.avatar_url) return data.avatar_url;
  if (typeof data.picture === 'string' && data.picture) return data.picture;
  return null;
}

export function getAuthUserInitials(user: User | null | undefined): string {
  const name = user?.user_metadata?.name;
  if (typeof name === 'string' && name.trim()) {
    return name
      .split(/\s+/)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  if (user?.email) return user.email.slice(0, 2).toUpperCase();
  return 'U';
}

interface UserAvatarProps {
  user: User | null | undefined;
  sizeClassName?: string;
  textClassName?: string;
  className?: string;
}

/**
 * Profile photo from Google auth when available, otherwise initials.
 */
const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  sizeClassName = 'w-9 h-9',
  textClassName = 'text-sm',
  className = '',
}) => {
  const avatarUrl = getAuthAvatarUrl(user);
  const [imgFailed, setImgFailed] = useState(false);
  const showPhoto = Boolean(avatarUrl) && !imgFailed;

  return (
    <div
      className={`${sizeClassName} rounded-full flex items-center justify-center overflow-hidden ${navigationColors.active.background} ${className}`}
    >
      {showPhoto ? (
        <img
          src={avatarUrl!}
          alt=""
          className="w-full h-full object-cover"
          referrerPolicy="no-referrer"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className={`text-white font-semibold ${textClassName}`}>
          {getAuthUserInitials(user)}
        </span>
      )}
    </div>
  );
};

export default UserAvatar;
